import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Drawer, useTheme } from '@prio365/prio365-react-library';
import { Card, Col, Popover, Row } from 'antd';
import Meta from 'antd/lib/card/Meta';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Flex from '../../../../components/Flex';
import SvgIcon from '../../../../components/SvgIcon';
import {
  AvailableProjectExtension,
  CreateProjectExtension,
  ProjectExtension,
} from '../../../../models/ProjectExtension';
import { SettingKey } from '../../../../models/Settings';
import { ProjectId } from '../../../../models/Types';
import { PrioTheme } from '../../../../theme/types';
import { makePrioStyles } from '../../../../theme/utils';
import { getSvgIconPathByType } from '../../../../util/icon';
import { apiFetchSettings } from '../../../settings/api';
import { apiFetchAvailableProjectExtensionsForProject } from '../../api';
import ProjectExtensionDrawer, {
  ProjectExtensionDrawerTabs,
} from './ProjectExtensionDrawer';

const useStyles = makePrioStyles((theme) => ({
  root: {
    '& > div': {
      '& > div': {
        '& > div': {
          '& > div': {
            display: 'flex',
            flexDirection: 'column',
          },
        },
      },
    },
    '& .ant-drawer-body': {
      display: 'flex',
      flexDirection: 'column',
    },
    height: '100%',
  },
  colGrid: {
    padding: theme.old.spacing.baseSpacing,
  },
  description: {
    margin: 'auto 0',
    '& p': {},
    '& h5': {
      color: theme.old.palette.chromaticPalette.lightGrey,
    },
  },
  secondaryText: {
    color: theme.old.palette.chromaticPalette.lightGrey,
  },
  grid: {
    display: 'flex',
    flexFlow: 'row wrap',
  },
  add: {
    position: 'absolute',
    margin: 0,
    width: '100%',
    height: '100%',
    opacity: 0,
    '&:hover': {
      opacity: 1,
    },
  },
  info: {
    position: 'absolute',
    margin: 0,
    width: '100%',
    height: '100%',
  },
  container: {},
  widget: {
    backgroundColor: theme.old.palette.backgroundPalette.content,
    boxShadow: theme.old.palette.boxShadow.regular,
    height: '100%',
    width: '100%',
    display: 'flex',
    minHeight: '150px',
    flexFlow: 'column',
    textAlign: 'center',
  },
  widgetInner: {
    width: '100%',
    height: '100%',
  },
  widgetInnerOverlay: {
    width: '100%',
    height: '100%',
    opacity: '0.9',
    backgroundColor: theme.old.palette.backgroundPalette.sub,
  },
  widgetOuterOverlayBackground: {
    width: '100%',
    height: '100%',
    paddingRight: theme.old.spacing.baseSpacing * 2,
    paddingBottom: theme.old.spacing.baseSpacing * 2,
  },

  disabled: {
    backgroundColor: theme.old.palette.backgroundPalette.sub,
  },
  selected: {
    backgroundColor: theme.old.palette.chromaticPalette.green,
    opacity: 0.5,
  },
  icon: {
    margin: '10px auto',
    fontSize: '24pt',
    opacity: 1,
  },
  iconAdd: {
    margin: 'auto auto',
    fontSize: '24pt',
    color: theme.old.palette.chromaticPalette.blue,
  },
  leftMargin: {
    marginLeft: '2px',
  },
}));

export interface AvailableProjectExtensionSelected
  extends AvailableProjectExtension {
  selected?: boolean;
}

interface AddProjectExtensionDrawerProps {
  projectId: ProjectId;
  addProjectExtensionDrawerVisible: boolean;
  setAddProjectExtensionDrawerVisible: (boolean) => void;
  reloadProjectExtension: () => void;
  existingProjectExtensions?: ProjectExtension[];
}

export const AddProjectExtensionDrawer: React.FC<
  AddProjectExtensionDrawerProps
> = (props) => {
  //#region ------------------------------ Defaults
  const {
    addProjectExtensionDrawerVisible,
    setAddProjectExtensionDrawerVisible,
    reloadProjectExtension,
    projectId,
    existingProjectExtensions,
  } = props;
  const classes = useStyles();
  const { t } = useTranslation();
  const theme = useTheme<PrioTheme>();
  //#endregion

  //#region ------------------------------ States / Attributes / Selectors
  const [projectExtensionDrawerVisible, setProjectExtensionDrawerVisible] =
    useState<boolean>(false);
  const [selectedAddProjectExtentions, setSelectedAddProjectExtentions] =
    useState<CreateProjectExtension>(null);
  const [projectExtensionDrawerTab, setProjectExtensionDrawerTab] =
    useState<ProjectExtensionDrawerTabs>('settings');

  const [availableProjectExtentions, setAvailableProjectExtentions] = useState<
    AvailableProjectExtensionSelected[]
  >([]);
  const [hasAmberSearchGlobalUrl, setHasAmberSearchGlobalUrl] =
    useState<boolean>(false);
  //#endregion

  //#region ------------------------------ Methods / Handlers
  const onClose = () => {
    setAddProjectExtensionDrawerVisible(false);
  };
  //#endregion

  //#region ------------------------------ Effects
  useEffect(() => {
    const fetchAmberSearchGlobalUrl = async () => {
      const { data } = await apiFetchSettings([
        {
          keys: ['ProjectExtension_AmberSearch_GlobalUrl'],
          eSettingScope: 'global',
        } as SettingKey,
      ]);
      const parsed = JSON.parse(data?.[0]?.jsonValue || '{}');
      const globalUrl = (parsed?.globalUrl ?? parsed?.GlobalUrl ?? '').trim();
      setHasAmberSearchGlobalUrl(!!globalUrl);
    };
    fetchAmberSearchGlobalUrl();
  }, []);

  useEffect(() => {
    if (addProjectExtensionDrawerVisible) {
      const loadConfigs = async () => {
        const { data } =
          await apiFetchAvailableProjectExtensionsForProject(projectId);
        if (data) {
          const availableExtensions: AvailableProjectExtensionSelected[] =
            data.map((extension) => {
              return { ...extension, selected: false };
            });
          setAvailableProjectExtentions(availableExtensions);
        }
      };
      loadConfigs();
    }
  }, [addProjectExtensionDrawerVisible, projectId]);
  //#endregion

  return (
    <Drawer
      closable={true}
      onClose={onClose}
      title={t('projects:addIn.extensions.addProjectExtensionTitle')}
      visible={addProjectExtensionDrawerVisible}
      footer={
        <Flex.Column justifyContent="flex-end">
          <Flex.Row justifyContent="flex-end">
            <Button
              onClick={() => {
                onClose();
              }}
            >
              {t('projects:createProject.buttons.back')}
            </Button>
          </Flex.Row>
        </Flex.Column>
      }
    >
      <Flex.Column childrenGap={theme.spacing.small}>
        <Flex.Row>
          <div>
            {t('projects:addIn.extensions.addProjectExtensionDescription')}
          </div>
        </Flex.Row>
        <Row gutter={[16, 16]} className={classes.grid}>
          {availableProjectExtentions
            .filter(
              (aExtensions) =>
                aExtensions.projectExtensionType === 'sharepoint' ||
                aExtensions.projectExtensionType === 'sharedMailbox' ||
                aExtensions.projectExtensionType === 'planner' ||
                aExtensions.projectExtensionType === 'jira' ||
                aExtensions.projectExtensionType === 'proman' ||
                aExtensions.projectExtensionType === 'openExperience' ||
                aExtensions.projectExtensionType === 'prioShare' ||
                (aExtensions.projectExtensionType === 'amberSearch' &&
                  hasAmberSearchGlobalUrl)
            )
            .map((availableExtension, index) => {
              const isAmberSearchAlreadyExists =
                availableExtension.projectExtensionType === 'amberSearch' &&
                existingProjectExtensions?.some(
                  (ext) => ext.projectExtensionType === 'amberSearch'
                );
              const isAvailable =
                availableExtension.projectExtensionAvailableState ===
                'available' && !isAmberSearchAlreadyExists;

              return (
                <Col
                  span={8}
                  className={classes.colGrid}
                  key={availableExtension.projectExtensionType + index}
                >
                  <Card
                    hoverable={
                      isAvailable && availableExtension.selected === false
                    }
                    className={
                      availableExtension.selected
                        ? classes.selected
                        : isAvailable
                          ? ''
                          : classes.disabled
                    }
                    onClick={() => {
                      if (isAvailable) {
                        setProjectExtensionDrawerVisible(true);
                        setSelectedAddProjectExtentions({
                          projectExtensionType:
                            availableExtension.projectExtensionType,
                          displayName: '',
                          value: '',
                        });
                      }
                    }}
                    cover={
                      <SvgIcon
                        width={60}
                        height={60}
                        path={getSvgIconPathByType(
                          availableExtension?.projectExtensionType
                        )}
                        style={{ marginTop: '20px' }}
                      ></SvgIcon>
                    }
                  >
                    <Meta
                      title={t(
                        `projects:addIn.${availableExtension?.projectExtensionType}`
                      )}
                      description={
                        isAmberSearchAlreadyExists ? (
                          <Flex.Row>
                            {t(`projects:addIn.notAvailable`)}{' '}
                            <Popover
                              content={t(
                                'projects:addIn.extensions.amberSearch.alreadyExistsExplanation'
                              )}
                            >
                              <FontAwesomeIcon
                                icon={['fal', 'info-circle']}
                                className={classes.leftMargin}
                              />
                            </Popover>
                          </Flex.Row>
                        ) : !isAvailable ? (
                          <Flex.Row>
                            {t(`projects:addIn.notAvailable`)}{' '}
                            <Popover
                              content={
                                availableExtension.stateExplanation ||
                                t(
                                  `projects:addIn.extensions.${availableExtension.projectExtensionType}.alreadyExistsExplanation`,
                                  { defaultValue: t(`projects:addIn.notAvailable`) }
                                )
                              }
                            >
                              <FontAwesomeIcon
                                icon={['fal', 'info-circle']}
                                className={classes.leftMargin}
                              />
                            </Popover>
                          </Flex.Row>
                        ) : (
                          t(`projects:addIn.addAddIn`)
                        )
                      }
                    />
                  </Card>
                </Col>
              );
            })}
        </Row>
      </Flex.Column>

      <ProjectExtensionDrawer
        projectExtensionDrawerVisible={projectExtensionDrawerVisible}
        setProjectExtensionDrawerVisible={setProjectExtensionDrawerVisible}
        projectExtension={null}
        addProjectExtension={selectedAddProjectExtentions}
        projectExtensionDrawerTab={projectExtensionDrawerTab}
        setProjectExtensionDrawerTab={setProjectExtensionDrawerTab}
        projectId={projectId}
        projectExtensionCreated={() => {
          setProjectExtensionDrawerVisible(false);
          setAddProjectExtensionDrawerVisible(false);
          if (reloadProjectExtension) {
            reloadProjectExtension();
          }
        }}
        projectExtensionUpdated={() => {
          //nothing to do
        }}
        reloadProjectExtension={reloadProjectExtension}
      />
    </Drawer>
  );
};

export default AddProjectExtensionDrawer;
